import { useCallback, useSyncExternalStore } from "react";

/**
 * Where the automatic-tactics switch is remembered. Browser-local, like the
 * clarifying-questions switch next to it: it is a way of working this client
 * carries on every request, not something the account has to hold — and two
 * neighbouring switches that behaved differently would be unreadable.
 */
const AUTO_TACTICS_STORAGE_KEY = "chat-auto-tactics-enabled";

/** Everything mounted that reads the switch, so one flip moves all of them. */
const subscribers = new Set<() => void>();

/**
 * The switch as this tab last saw it.
 *
 * Cached rather than read from storage on every render: `useSyncExternalStore`
 * compares snapshots by identity and calls the getter on each render, and
 * storage can also throw — see {@link readStoredValue}.
 */
let currentValue: boolean = readStoredValue();

/**
 * The switch as storage holds it, read as an opt-out: anything but an explicit
 * `false` leaves the tactics automatic.
 *
 * Reading is what throws, not only writing: Safari in private browsing and a
 * profile with cookies blocked both raise on `getItem`, and a throw here would
 * take down the composer, which is the app.
 */
function readStoredValue(): boolean {
  try {
    return window.localStorage.getItem(AUTO_TACTICS_STORAGE_KEY) !== "false";
  } catch {
    return true;
  }
}

/**
 * Whether the assistant may reach for the user's tactics on its own.
 *
 * Exported for the send paths that are not React — the chat transport reads it
 * as it builds each request, so the value that goes out is the one on screen
 * when the message left, whichever surface sent it.
 *
 * @returns Whether automatic tactics are on.
 */
export function readAutoTacticsSetting(): boolean {
  return currentValue;
}

/**
 * The chat's "automatic tactics" switch, and the only place that reads or
 * writes it.
 *
 * Kept in one module-level value rather than in each component's state: the
 * composer and its mobile twin can be mounted at the same time, and two copies
 * of the same switch would disagree the moment one of them was flipped.
 *
 * @returns Whether automatic tactics are on, and a setter that records the
 *   change.
 */
export function useAutoTacticsSetting(): {
  readonly isEnabled: boolean;
  readonly setIsEnabled: (isEnabled: boolean) => void;
} {
  const isEnabled = useSyncExternalStore(subscribe, readAutoTacticsSetting);

  const setIsEnabled = useCallback((nextValue: boolean): void => {
    currentValue = nextValue;
    try {
      window.localStorage.setItem(
        AUTO_TACTICS_STORAGE_KEY,
        String(nextValue),
      );
    } catch {
      // Unwritable storage is nothing to interrupt a message for: the switch
      // holds for this session and comes back on in the next one.
    }
    subscribers.forEach((notify) => notify());
  }, []);

  return { isEnabled, setIsEnabled };
}

/**
 * Registers a reader of the switch.
 *
 * @param notify Called after every flip.
 * @returns The unsubscribe function `useSyncExternalStore` expects.
 */
function subscribe(notify: () => void): () => void {
  subscribers.add(notify);
  return () => {
    subscribers.delete(notify);
  };
}
